import React, { useMemo, useState } from "react";
import Icon from "@mdi/react";
import { mdiPlus, mdiDelete } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { fetchPut } from "../../content_panel/services/serviceClient";
import NewItemSearchByIdField from "../../../../erp/inventory/items/item_search_by_ids";
import { GetItemWAC } from "../warehouse_module_repo";

type ReplacementItem = {
  item_code: string;
  item_name: string;
  qty: number;
  price: number;
  subCategory: string;
};

type Props = {
  outward: any;
  fromWarehouse?: string;
  availableItemCodes?: string[];
  onClose: () => void;
  onSaved: (items: ReplacementItem[]) => void;
};

const ReplacementItemsModal: React.FC<Props> = ({
  outward,
  fromWarehouse,
  availableItemCodes,
  onClose,
  onSaved,
}) => {
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedItemPrice, setSelectedItemPrice] = useState<number>(0);
  const [loadingPrice, setLoadingPrice] = useState(false);
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();

  // existing replacement items (if already saved earlier)
  const [replacementItems, setReplacementItems] = useState<ReplacementItem[]>(
    outward?.items?.map((i: any) => ({
      item_code: i.inventoryItemCode,
      item_name: i.itemName,
      qty: i.requestedQty,
      price: i.itemPrice ?? 0,
      subCategory: i.subCategory ?? "",
    })) || [],
  );

  /* ---------------- Faulty qty ---------------- */

  const totalFaultyQty = useMemo(() => {
    return (
      outward?.faulty_items?.reduce(
        (sum: number, item: any) => sum + (item.requestedQty || 0),
        0,
      ) || 0
    );
  }, [outward]);

  const totalReplacementQty = useMemo(() => {
    return replacementItems.reduce((sum, i) => sum + i.qty, 0);
  }, [replacementItems]);

  /* ---------------- Fetch WAC on item select ---------------- */

  const handleItemSelect = async (item: any) => {
    setSelectedItem(item);
    setSelectedItemPrice(0);

    if (!item?.item_code) return;

    try {
      setLoadingPrice(true);
      const res = await GetItemWAC(item.item_code);
      if (res?.success && res?.data?.wac_value) {
        setSelectedItemPrice(res.data.wac_value);
      }
    } catch {
      // price stays 0
    } finally {
      setLoadingPrice(false);
    }
  };

  /* ---------------- Add item ---------------- */

  const addItem = () => {
    if (!selectedItem) return;

    const remainingQty = totalFaultyQty - totalReplacementQty;

    if (remainingQty <= 0) {
      showToast({
        type: ToastType.warning,
        text: "Replacement quantity already fulfilled",
      });
      return;
    }

    const newItem: ReplacementItem = {
      item_code: selectedItem.item_code,
      item_name: selectedItem.name,
      qty: remainingQty,
      price: selectedItemPrice,
      subCategory: selectedItem.subcategory ?? "",
    };

    setReplacementItems((prev) => [...prev, newItem]);
    setSelectedItem(null);
    setSelectedItemPrice(0);
  };

  const removeItem = (index: number) => {
    setReplacementItems((prev) => prev.filter((_, i) => i !== index));
  };

  /* ---------------- Save ---------------- */

  const handleSave = async () => {
    if (totalReplacementQty !== totalFaultyQty) {
      showToast({
        type: ToastType.error,
        text: "Replacement qty must equal faulty qty",
      });
      return;
    }

    try {
      setLoading(true);

      await fetchPut(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/update-replacement-items/${outward.id}`,
        { items: replacementItems },
      );

      showToast({
        type: ToastType.success,
        text: "Replacement items saved",
      });

      onSaved(replacementItems);
    } catch (err: any) {
      showToast({
        type: ToastType.error,
        text: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- UI ---------------- */

  return (
    <div
      id="warehouse-outward-replacement-items-modal"
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white w-full max-w-[480px] rounded-2xl shadow-2xl overflow-hidden wh-modal-xs max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <h2 className="text-base font-semibold text-gray-900">
            Replace With Another Item
          </h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Match replacement quantity to faulty quantity
          </p>
        </div>

        <div className="px-5 pb-4 space-y-3">
          {/* Faulty Items */}
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
            <p className="text-[11px] font-medium text-red-400 uppercase tracking-wider mb-1.5">
              Faulty
            </p>
            {outward.faulty_items?.map((item: any, i: number) => (
              <div
                key={i}
                className="flex justify-between items-center text-sm"
              >
                <span className="text-gray-800 font-medium truncate mr-3">
                  {item.itemName}
                </span>
                <span className="text-red-600 font-semibold text-xs bg-red-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                  Qty: {item.requestedQty}
                </span>
              </div>
            ))}
          </div>

          {/* Search + Add */}
          <div>
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-1.5">
              Replacement
            </p>
            <div className="flex gap-2">
              <div
                id="warehouse-outward-replacement-item-search-wrapper"
                className="flex-1"
              >
                <NewItemSearchByIdField
                  disabled={!fromWarehouse}
                  value={selectedItem || ({} as any)}
                  placeholder="Search item..."
                  onSelect={handleItemSelect}
                  item_codes={
                    availableItemCodes?.length ? availableItemCodes : undefined
                  }
                />
              </div>
              <button
                id="warehouse-outward-replacement-add-item-btn"
                onClick={addItem}
                disabled={!selectedItem}
                className="w-9 h-9 flex items-center justify-center bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors shrink-0"
              >
                <Icon path={mdiPlus} size={0.7} />
              </button>
            </div>
            {selectedItem && (
              <p className="text-[11px] text-gray-400 mt-1 pl-1">
                {loadingPrice
                  ? "Fetching price..."
                  : `WAC: ₹${selectedItemPrice.toFixed(2)}`}
              </p>
            )}
          </div>

          {/* Replacement Items List */}
          {replacementItems.length > 0 && (
            <div className="space-y-1.5">
              {replacementItems.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2"
                >
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {item.item_name}
                    </p>
                    <p className="text-xs text-gray-400">
                      Qty: {item.qty} &middot; ₹{item.price.toFixed(2)}
                    </p>
                  </div>
                  <button
                    id={`warehouse-outward-replacement-remove-item-${i}`}
                    onClick={() => removeItem(i)}
                    className="text-gray-300 hover:text-red-500 transition-colors shrink-0"
                  >
                    <Icon path={mdiDelete} size={0.6} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Qty summary bar */}
          <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
            <span className="text-xs text-gray-500">Replacement Qty</span>
            <span
              className={`text-sm font-semibold ${
                totalReplacementQty === totalFaultyQty
                  ? "text-green-600"
                  : "text-amber-500"
              }`}
            >
              {totalReplacementQty} / {totalFaultyQty}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-5 py-3 flex justify-end gap-2">
          <button
            id="warehouse-outward-replacement-cancel-btn"
            onClick={onClose}
            className="px-4 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            id="warehouse-outward-replacement-save-btn"
            onClick={handleSave}
            disabled={loading}
            className="px-5 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors font-medium"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReplacementItemsModal;
